import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../lang/useLanguage';

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const tok = () => localStorage.getItem('token');
const hdrs = () => ({ Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json' });

const STATUS_COLOR = {
  assembly_pending: '#f59e0b',
  assembly_in_progress: '#06b6d4',
  assembly_done: '#8b5cf6',
  assembly_completed: '#8b5cf6',
  qc_pending: '#f97316',
  final_qc_pending: '#8b5cf6',
  final_qc_passed: '#10b981',
  final_qc_failed: '#ef4444',
  in_progress: '#06b6d4',
  completed: '#10b981',
  cancelled: '#ef4444',
};

const STATUS_BG = {
  assembly_pending: 'rgba(245,158,11,0.12)',
  assembly_in_progress: 'rgba(6,182,212,0.12)',
  assembly_done: 'rgba(139,92,246,0.12)',
  assembly_completed: 'rgba(139,92,246,0.12)',
  qc_pending: 'rgba(249,115,22,0.12)',
  final_qc_pending: 'rgba(139,92,246,0.12)',
  final_qc_passed: 'rgba(16,185,129,0.12)',
  final_qc_failed: 'rgba(239,68,68,0.12)',
  in_progress: 'rgba(6,182,212,0.12)',
  completed: 'rgba(16,185,129,0.12)',
  cancelled: 'rgba(239,68,68,0.12)',
};

const StatusBadge = ({ status, labels }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: STATUS_BG[status] || 'rgba(6,182,212,0.12)',
    color: STATUS_COLOR[status] || '#06b6d4',
    border: `1px solid ${(STATUS_COLOR[status] || '#64748b')}44`,
    whiteSpace: 'nowrap',
  }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLOR[status] || '#06b6d4' }} />
    {labels?.[status] || status}
  </span>
);

export default function Assembly() {
  const { t } = useTheme();
  const { t: lang } = useLanguage();
  const a = lang.assemblyPage;

  const statusLabels = {
    draft: a.statusDraft,
    planned: a.statusPlanned,
    released: a.statusReleased,
    in_progress: a.statusInProgress,
    qc_pending: a.statusQcPending,
    qc_passed: a.statusQcPassed,
    assembly_pending: a.statusAssemblyPending,
    assembly_in_progress: a.statusAssemblyInProgress,
    assembly_done: a.statusAssemblyDone,
    assembly_completed: a.statusAssemblyCompleted,
    final_qc_pending: a.statusFinalQcPending,
    final_qc_passed: a.statusFinalQcPassed,
    final_qc_failed: a.statusFinalQcFailed,
    final_qc: a.statusFinalQc,
    completed: a.statusCompleted,
    cancelled: a.statusCancelled,
  };
  const [activeOrders, setActiveOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(25);
  const [selected, setSelected] = useState(null);
  const [, setSubOrders] = useState([]);
  const [assemblyMaterials, setAssemblyMaterials] = useState(null);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completedQty, setCompletedQty] = useState('');
  const [notes, setNotes] = useState('');
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/work-orders/assembly/list`, { headers: hdrs() });
      const d = await r.json();
      const all = Array.isArray(d) ? d : [];
      // Aktif montaj emirlerini al; bunların parent/root iş emirlerini de ekranda tut.
      // Böylece Pano gibi alt montaj aktifken ana iş emri kaybolmaz.
      const activeStatuses = ['in_progress','assembly_pending','assembly_in_progress','assembly_done','assembly_completed','final_qc_pending','final_qc_failed'];
      const activeBase = all.filter(w => activeStatuses.includes(w.status));
      const activeWithParents = [...activeBase];

      const addParentChain = (orders) => {
        orders.forEach(w => {
          if (w.parent_order_no && !activeWithParents.find(x => x.order_no === w.parent_order_no)) {
            const parent = all.find(x => x.order_no === w.parent_order_no);
            if (parent) {
              activeWithParents.push(parent);
              addParentChain([parent]);
            }
          }
        });
      };

      addParentChain(activeBase);

      activeWithParents.sort((a, b) => {
        const aRoot = a.root_order_id || a.parent_order_id || a.id;
        const bRoot = b.root_order_id || b.parent_order_id || b.id;
        if (aRoot !== bRoot) return bRoot - aRoot;
        const aLevel = a.level_no || 0;
        const bLevel = b.level_no || 0;
        if (aLevel !== bLevel) return aLevel - bLevel;
        return (a.order_no || '').localeCompare(b.order_no || '');
      });

      setActiveOrders(activeWithParents);
      const completedOrders = all.filter(w => ['completed','final_qc_passed'].includes(w.status));
      const combined = [...completedOrders];
      const addParents = (orders) => {
        orders.forEach(w => {
          if (w.parent_order_no && !combined.find(x => x.order_no === w.parent_order_no)) {
            const parent = all.find(x => x.order_no === w.parent_order_no);
            if (parent) { combined.push(parent); addParents([parent]); }
          }
        });
      };
      addParents([...completedOrders]);

      combined.sort((a, b) => {
        const aRoot = a.root_order_id || a.parent_order_id || a.id;
        const bRoot = b.root_order_id || b.parent_order_id || b.id;

        if (aRoot !== bRoot) return bRoot - aRoot;

        const aLevel = a.level_no || 0;
        const bLevel = b.level_no || 0;

        if (aLevel !== bLevel) return aLevel - bLevel;

        return (a.order_no || '').localeCompare(b.order_no || '');
      });

      setHistoryOrders(combined);
    } catch { setActiveOrders([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSubs = async (orderId) => {
    try {
      const r = await fetch(`${API}/work-orders/${orderId}/sub-orders`, { headers: hdrs() });
      const d = await r.json();
      setSubOrders(Array.isArray(d) ? d : []);
    } catch { setSubOrders([]); }
  };

  const loadAssemblyMaterials = async (orderId) => {
    setLoadingMaterials(true);
    setAssemblyMaterials(null);
    try {
      const r = await fetch(`${API}/work-orders/${orderId}/assembly-materials`, { headers: hdrs() });
      if (r.ok) { const d = await r.json(); setAssemblyMaterials(d); }
    } catch { setAssemblyMaterials(null); }
    setLoadingMaterials(false);
  };

  const selectOrder = (wo) => {
    setSelected(wo);
    setCompletedQty(String(wo.quantity || ''));
    setNotes('');
    setCompleting(false);
    loadSubs(wo.id);

    // Montaj iş emrinin malzemelerini her zaman yükle.
    // Backend boşsa boş döner; Pano gibi assembly_only ürünlerde BOM'dan depo malzemeleri gelir.
    loadAssemblyMaterials(wo.id);
  };

  const startAssembly = async (wo) => {
    try {
      const r = await fetch(`${API}/work-orders/${wo.id}/assembly-start`, {
        method: 'PATCH', headers: hdrs(),
        body: JSON.stringify({ assigned_to: localStorage.getItem('username') })
      });
      const d = await r.json();
      if (r.ok) { showMsg('success', a.assemblyStarted); load(); if (selected?.id === wo.id) setSelected({ ...wo, status: 'assembly_in_progress' }); }
      else showMsg('error', d.detail || a.error);
    } catch { showMsg('error', a.connectionError); }
  };

  const completeAssembly = async () => {
    if (!selected) return;
    try {
      const r = await fetch(`${API}/work-orders/assembly/${selected.id}/complete`, {
        method: 'PATCH', headers: hdrs(),
        body: JSON.stringify({ completed_quantity: parseInt(completedQty), notes })
      });
      const d = await r.json();
      if (r.ok) { showMsg('success', d.message || a.assemblyCompletedSuccess); setSelected(null); load(); }
      else showMsg('error', d.detail || a.error);
    } catch { showMsg('error', a.connectionError); }
  };

  const requestMaterialFromWarehouse = async (mat) => {
    if (!selected || !mat) return;
    try {
      const r = await fetch(`${API}/stock-requests`, {
        method: 'POST',
        headers: hdrs(),
        body: JSON.stringify({
          material_name: mat.material_name,
          quantity: mat.quantity,
          unit: mat.unit,
          request_type: 'assembly',
          work_order_id: selected.id,
          work_order_no: selected.order_no,
          department: 'Montaj',
          machine: selected.product_code || '',
          reason: 'Montaj malzeme talebi'
        })
      });
      const d = await r.json();
      if (r.ok) {
        showMsg('success', d.message || a.warehouseRequestSent);
        loadAssemblyMaterials(selected.id);
      } else {
        showMsg('error', d.detail || a.requestCreateFailed);
      }
    } catch {
      showMsg('error', a.connectionError);
    }
  };


  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3500);
  };

  const getProductionItemLabel = (item) => {
    return item.tamamlandi ? a.ready : a.waiting;
  };

  const buildHierarchy = (list) => {
    const roots = list.filter(w => !w.parent_order_no);
    const result = [];
    const addChildren = (orderNo, depth) => {
      list.filter(w => w.parent_order_no === orderNo).forEach(child => {
        result.push({ ...child, _depth: depth });
        addChildren(child.order_no, depth + 1);
      });
    };
    roots.forEach(root => {
      result.push({ ...root, _depth: 0 });
      addChildren(root.order_no, 1);
    });
    if (roots.length === 0) list.forEach(w => result.push({ ...w, _depth: 0 }));
    return result;
  };

  const activeHierarchy = buildHierarchy(activeOrders);
  const historyHierarchy = buildHierarchy(historyOrders);

  const cardBg = t?.bg?.card || '#0f172a';
  const metricBg = t?.bg?.metricCard || '#1e293b';
  const borderDefault = t?.border?.default || '#334155';
  const textPrimary = t?.text?.primary || '#e2e8f0';
  const textMuted = t?.text?.muted || '#64748b';
  const textSecondary = t?.text?.secondary || '#06b6d4';

  const renderOrderCard = (wo, isActive = true) => {
    const depth = wo._depth || 0;
    const isRoot = depth === 0;
    const isSelected = selected?.id === wo.id;
    const indent = depth * 20;
    return (
      <div key={wo.id}
        onClick={() => selectOrder(wo)}
        style={{
          background: isSelected ? metricBg : cardBg,
          border: `1px solid ${isSelected ? '#3b82f6' : isRoot ? (isActive ? '#3b82f655' : '#10b98133') : '#3b82f633'}`,
          borderLeft: isRoot ? `3px solid ${isActive ? '#3b82f6' : '#10b981'}` : `3px solid #3b82f6`,
          borderRadius: 10,
          padding: `12px 16px 12px ${16 + indent}px`,
          marginBottom: 6,
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {depth > 0 && <span style={{ color: textMuted, fontSize: 12 }}>└─</span>}
            <div>
              <div style={{ fontWeight: isRoot ? 800 : 600, fontSize: isRoot ? 14 : 13, color: isRoot ? (isActive ? '#3b82f6' : '#10b981') : '#60a5fa' }}>
                {wo.order_no}
              </div>
              <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
                {wo.product_code}{wo.product_name ? ` · ${wo.product_name}` : ''}
                {wo.customer ? ` · ${wo.customer}` : ''}
                {` · ${wo.quantity} ${a.piece}`}
              </div>

            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <StatusBadge status={wo.status} labels={statusLabels} />
            {isActive && isRoot && wo.status === 'assembly_pending' && (
              <button onClick={e => { e.stopPropagation(); startAssembly(wo); }}
                style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                ▶ {a.startAssembly}
              </button>
            )}
          </div>
        </div>

        {isSelected && assemblyMaterials?.depodan_alinacaklar?.length > 0 && (
          <div style={{ marginTop: 10, marginLeft: indent + 24, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {assemblyMaterials.depodan_alinacaklar.map((m, i) => (
              <div key={`${m.material_code}-${i}`} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 10px',
                borderRadius: 8,
                background: m.yeterli ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)',
                border: `1px solid ${m.yeterli ? '#10b98133' : '#f59e0b33'}`,
                fontSize: 11
              }}>
                <span style={{ color: m.yeterli ? '#10b981' : '#f59e0b', fontWeight: 700 }}>
                  └─ 📦 {m.material_code} · {m.material_name}
                </span>
                <span style={{ color: m.yeterli ? '#10b981' : '#f59e0b', fontWeight: 700 }}>
                  {m.yeterli ? a.warehouseReady : a.missing} · {m.quantity} {m.unit || a.piece}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: 24, color: textPrimary, minHeight: '100vh', background: t?.bg?.page || '#0a0f1e' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>🔧 {a.title}</h2>
          <p style={{ margin: '4px 0 0', color: textSecondary, fontSize: 13 }}>{a.subtitle}</p>
        </div>
        <button onClick={load} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          🔄 {a.refresh}
        </button>
      </div>

      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: msg.type === 'success' ? '#10b98122' : '#ef444422',
          color: msg.type === 'success' ? '#10b981' : '#ef4444',
          border: `1px solid ${msg.type === 'success' ? '#10b98144' : '#ef444444'}`
        }}>{msg.text}</div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40, color: textMuted }}>⏳ {a.loading}</div>}

      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.2fr' : '1fr', gap: 20 }}>
          {/* Sol: Liste */}
          <div>
            {activeHierarchy.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: textMuted, background: cardBg, borderRadius: 14, border: `1px solid ${borderDefault}` }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔧</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{a.noReadyWorkOrders}</div>
                <div style={{ fontSize: 13, marginTop: 8 }}>{a.noReadyWorkOrdersHint}</div>
              </div>
            ) : (
              activeHierarchy.map(wo => renderOrderCard(wo, true))
            )}

            {historyOrders.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <button onClick={() => setShowHistory(h => !h)}
                  style={{ width: '100%', padding: '10px 16px', borderRadius: 10, border: `1px solid ${borderDefault}`, background: metricBg, color: textSecondary, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showHistory ? 12 : 0 }}>
                  <span>📋 {a.completedAssemblies} ({historyOrders.filter(w => ['completed','final_qc_passed'].includes(w.status)).length})</span>
                  <span>{showHistory ? `▲ ${a.hide}` : `▼ ${a.show}`}</span>
                </button>
                {showHistory && (() => {
                  const totalPages = Math.ceil(historyHierarchy.length / historyPageSize);
                  const paged = historyHierarchy.slice((historyPage-1)*historyPageSize, historyPage*historyPageSize);
                  return (
                    <div>
                      {paged.map(wo => renderOrderCard(wo, false))}
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:12,flexWrap:'wrap',gap:8}}>
                        <div style={{display:'flex',gap:6,alignItems:'center'}}>
                          <span style={{color:textMuted,fontSize:12}}>{a.perPage}:</span>
                          {[25,50,100].map(n=>(
                            <button key={n} onClick={()=>{setHistoryPageSize(n);setHistoryPage(1);}} style={{padding:'4px 12px',borderRadius:8,border:`1px solid ${borderDefault}`,background:historyPageSize===n?'#10b981':'transparent',color:historyPageSize===n?'#fff':textMuted,cursor:'pointer',fontSize:12,fontWeight:600}}>{n}</button>
                          ))}
                        </div>
                        <div style={{display:'flex',gap:4,alignItems:'center'}}>
                          <button onClick={()=>setHistoryPage(1)} disabled={historyPage===1} style={{padding:'4px 8px',borderRadius:6,border:`1px solid ${borderDefault}`,background:'transparent',color:textMuted,cursor:'pointer',fontSize:12}}>«</button>
                          <button onClick={()=>setHistoryPage(p=>Math.max(1,p-1))} disabled={historyPage===1} style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${borderDefault}`,background:'transparent',color:textPrimary,cursor:'pointer',fontSize:14}}>‹</button>
                          {Array.from({length:Math.min(5,totalPages)},(_,i)=>{
                            let page=i+1;
                            if(totalPages>5){if(historyPage<=3)page=i+1;else if(historyPage>=totalPages-2)page=totalPages-4+i;else page=historyPage-2+i;}
                            return <button key={page} onClick={()=>setHistoryPage(page)} style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${page===historyPage?'#10b981':borderDefault}`,background:page===historyPage?'#10b981':'transparent',color:page===historyPage?'#fff':textPrimary,cursor:'pointer',fontSize:12,fontWeight:600,minWidth:32}}>{page}</button>;
                          })}
                          <button onClick={()=>setHistoryPage(p=>Math.min(totalPages,p+1))} disabled={historyPage>=totalPages} style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${borderDefault}`,background:'transparent',color:textPrimary,cursor:'pointer',fontSize:14}}>›</button>
                          <button onClick={()=>setHistoryPage(totalPages)} disabled={historyPage>=totalPages} style={{padding:'4px 8px',borderRadius:6,border:`1px solid ${borderDefault}`,background:'transparent',color:textMuted,cursor:'pointer',fontSize:12}}>»</button>
                        </div>
                        <span style={{color:textMuted,fontSize:12}}>{historyHierarchy.length} {a.records}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Sağ: Detay Paneli */}
          {selected && (
            <div style={{ background: cardBg, border: `1px solid ${borderDefault}`, borderRadius: 14, padding: 20, maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#3b82f6' }}>📋 {selected.order_no}</h3>
                  <div style={{ fontSize: 12, color: textSecondary, marginTop: 4 }}>
                    {selected.product_code}{selected.product_name ? ` · ${selected.product_name}` : ''} · {selected.customer || '—'} · {selected.quantity} {a.piece}
                  </div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: textMuted, cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>

              {/* Durum */}
              <div style={{ marginBottom: 16, padding: '10px 14px', background: metricBg, borderRadius: 10, border: `1px solid ${borderDefault}` }}>
                <div style={{ fontSize: 11, color: textMuted, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>{a.currentStatus}</div>
                <StatusBadge status={selected.status} labels={statusLabels} />
              </div>

              {/* Malzeme Listesi */}
              {loadingMaterials && (
                <div style={{ color: textMuted, fontSize: 12, padding: 8, textAlign: 'center' }}>⏳ {a.materialsLoading}</div>
              )}

              {assemblyMaterials && !loadingMaterials && (
                <div style={{ marginBottom: 16 }}>
                  {/* Genel durum özeti */}
                  {assemblyMaterials.depo_tamam && assemblyMaterials.uretim_tamam ? (
                    <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(16,185,129,0.1)', border: '1px solid #10b98133', borderRadius: 8, fontSize: 12, color: '#10b981', fontWeight: 600 }}>
                      ✅ {a.allMaterialsReady}
                    </div>
                  ) : (
                    <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef444433', borderRadius: 8, fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                      ⚠️ {a.missingMaterialPurchaseRequired}
                    </div>
                  )}

                  {/* Üretimden Gelenler */}
                  {(assemblyMaterials.uretimden_gelenler || []).length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: '#10b981', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
                        🏭 {a.productionMaterials}
                      </div>
                      {assemblyMaterials.uretimden_gelenler.map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, marginBottom: 4, background: item.tamamlandi ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)', border: `1px solid ${item.tamamlandi ? '#10b98133' : '#f59e0b33'}` }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: item.tamamlandi ? '#10b981' : '#f59e0b' }}>{item.product_code}{item.product_name ? ` · ${item.product_name}` : ''}</div>
                            <div style={{ fontSize: 11, color: textMuted }}>{item.quantity} {a.piece}</div>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: item.tamamlandi ? '#10b981' : '#f59e0b' }}>
                            {getProductionItemLabel(item)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Depodan Alınacaklar */}
                  {(assemblyMaterials.depodan_alinacaklar || []).length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, color: '#3b82f6', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase' }}>
                        📦 {a.warehouseMaterials}
                      </div>
                      {assemblyMaterials.depodan_alinacaklar.map((mat, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, marginBottom: 4, background: mat.yeterli ? 'rgba(59,130,246,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${mat.yeterli ? '#3b82f633' : '#ef444433'}` }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: mat.yeterli ? textPrimary : '#ef4444' }}>{mat.material_name}</div>
                            <div style={{ fontSize: 11, color: textMuted }}>
                              {a.required}: <strong>{mat.quantity} {mat.unit}</strong> · {a.inWarehouse}: <strong style={{ color: mat.yeterli ? '#10b981' : '#ef4444' }}>{mat.stock_qty} {mat.unit}</strong>
                            </div>
                          </div>
                          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
                            <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', marginLeft: 8,
                              color: mat.teslim_alindi ? '#10b981' : mat.yeterli ? '#10b981' : '#ef4444' }}>
                              {mat.teslim_alindi
                                ? a.delivered
                                : mat.request_status === 'pending'
                                  ? a.requestSent
                                  : mat.yeterli
                                    ? a.inStock
                                    : a.outOfStock}
                            </span>

                            {!mat.teslim_alindi && mat.request_status !== 'pending' && (
                              <button
                                style={{
                                  padding:'4px 10px',
                                  borderRadius:6,
                                  border:'none',
                                  background:'#3b82f6',
                                  color:'#fff',
                                  cursor:'pointer',
                                  fontSize:11,
                                  fontWeight:600
                                }}
                                onClick={() => requestMaterialFromWarehouse(mat)}
                              >
                                {a.request}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Montaj İşlemleri */}
              {selected.status === 'assembly_pending' && (
                <button onClick={() => startAssembly(selected)}
                  style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                  ▶ {a.startAssembly}
                </button>
              )}

              {selected.status === 'assembly_in_progress' && (
                <div>
                  {!completing ? (
                    <button onClick={() => setCompleting(true)}
                      style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                      ✅ {a.completeAssembly}
                    </button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>{a.completedQuantity}</label>
                        <input type="number" value={completedQty} onChange={e => setCompletedQty(e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', background: metricBg, border: `1px solid ${borderDefault}`, borderRadius: 8, color: textPrimary, fontSize: 13, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>{a.notes}</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                          style={{ width: '100%', padding: '10px 12px', background: metricBg, border: `1px solid ${borderDefault}`, borderRadius: 8, color: textPrimary, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setCompleting(false)}
                          style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1px solid ${borderDefault}`, background: 'none', color: textSecondary, cursor: 'pointer', fontSize: 13 }}>
                          {a.cancel}
                        </button>
                        <button onClick={completeAssembly}
                          style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                          ✅ {a.approve}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selected.status === 'final_qc_pending' && (
                <div style={{ padding: '14px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#8b5cf6' }}>{a.finalQcWaiting}</div>
                  <div style={{ fontSize: 12, color: textMuted, marginTop: 6 }}>{a.finalQcWaitingHint}</div>
                </div>
              )}

              {selected.status === 'completed' && (
                <div style={{ padding: '14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>{a.finalQcPassedCompleted}</div>
                  <div style={{ fontSize: 12, color: textMuted, marginTop: 6 }}>{a.completedReadyShipment}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}


    </div>
  );
}